import * as pdfjsLib from "pdfjs-dist";
// Worker Vite (bundler-safe) — pdfjs-dist v6
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

import type { LayoutItem, LayoutLink, LayoutPage } from "./extract";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

/**
 * Extraction PDF côté client :
 *  - `pdfGetItems` : texte natif avec positions réelles (getTextContent) —
 *    précis et instantané, SANS OCR, double colonne géré par le serveur ;
 *  - `pdfRenderCanvases` : rendu page par page (pour l'OCR des scans).
 */

const BOLD_FONT = /bold|black|heavy|semibold|demi|w[5-9]00/i;

// Le vrai nom de la police devient disponible après getOperatorList.
const realFontName = (page: pdfjsLib.PDFPageProxy, fontName: string): string => {
  try {
    if (!page.commonObjs.has(fontName)) return "";
    const font = page.commonObjs.get(fontName) as { name?: unknown } | null;
    return typeof font?.name === "string" ? font.name.replace(/^[A-Z]{6}\+/, "") : "";
  } catch {
    return "";
  }
};

export type PdfTextExtraction = {
  pages: LayoutPage[];
  charCount: number;
};

export const pdfGetItems = async (
  file: File,
  onProgress?: (ratio: number) => void,
): Promise<PdfTextExtraction> => {
  const data = new Uint8Array(await file.arrayBuffer());
  const doc = await pdfjsLib.getDocument({ data }).promise;
  const pages: LayoutPage[] = [];
  let charCount = 0;

  for (let pageNo = 1; pageNo <= doc.numPages; pageNo++) {
    const page = await doc.getPage(pageNo);
    const viewport = page.getViewport({ scale: 1 });
    const content = await page.getTextContent();
    await page.getOperatorList();
    const styles = content.styles as Record<string, { fontFamily?: string }>;
    const fonts = new Map<string, string>();
    const items: LayoutItem[] = [];

    for (const raw of content.items) {
      const text = String((raw as { str?: string }).str ?? "");
      if (!text.trim()) continue;
      const transform = (raw as { transform?: number[] }).transform ?? [0, 0, 0, 0, 0, 0];
      const fontName = String((raw as { fontName?: string }).fontName ?? "");
      const height = Math.abs(Number((raw as { height?: number }).height ?? 0)) || 10;
      if (!fonts.has(fontName)) fonts.set(fontName, realFontName(page, fontName));
      const font = fonts.get(fontName) || styles[fontName]?.fontFamily || "";
      items.push({
        text,
        x: transform[4] ?? 0,
        top: viewport.height - (transform[5] ?? 0),
        width: Math.abs(Number((raw as { width?: number }).width ?? 0)),
        height,
        bold: BOLD_FONT.test(font),
        font: font.slice(0, 80),
      });
      charCount += text.length;
    }

    const links: LayoutLink[] = [];
    for (const annotation of await page.getAnnotations()) {
      const { subtype, url, rect } = annotation as {
        subtype?: string;
        url?: unknown;
        rect?: number[];
      };
      if (subtype !== "Link" || typeof url !== "string" || !rect || links.length >= 100) continue;
      const [x1, y1, x2, y2] = rect;
      links.push({
        url: url.slice(0, 500),
        x: Math.min(x1, x2),
        top: viewport.height - Math.max(y1, y2),
        width: Math.abs(x2 - x1),
        height: Math.abs(y2 - y1),
      });
    }

    pages.push({ width: viewport.width, height: viewport.height, items, links });
    page.cleanup();
    onProgress?.(pageNo / doc.numPages);
  }

  return { pages, charCount };
};

/** Rend chaque page PDF sur un canvas haute résolution (pour l'OCR des scans). */
export const pdfRenderCanvases = async (
  file: File,
  scale = 2,
  onProgress?: (done: number, total: number) => void,
): Promise<HTMLCanvasElement[]> => {
  const data = new Uint8Array(await file.arrayBuffer());
  const doc = await pdfjsLib.getDocument({ data }).promise;
  const canvases: HTMLCanvasElement[] = [];

  for (let pageNo = 1; pageNo <= doc.numPages; pageNo++) {
    const page = await doc.getPage(pageNo);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("Canvas indisponible");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvas, canvasContext: ctx, viewport }).promise;
    page.cleanup();
    canvases.push(canvas);
    onProgress?.(pageNo, doc.numPages);
  }

  return canvases;
};
